import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { resolveMediaUrl } from "@/lib/utils";
import type {
	DashboardDeskDesigner,
	DashboardProposal,
} from "../services/homeowner-dashboard-api.service";
import { IMAGE_PLACEHOLDER, swapToPlaceholder } from "../utils/image-fallback";
import { getAvatarStyle } from "./dashboard-primitives";

// ============================================================================
// Desk Threads (Proposal, Designer, Site Walk)
// ============================================================================

interface DeskThreadsProps {
	proposal: DashboardProposal;
	designer: DashboardDeskDesigner | null;
	onDecline?: (proposalId: string) => void;
	onOpenProposal?: (projectId: string) => void;
	onMessageDesigner?: (projectId: string) => void;
	isDeclining?: boolean;
}

export function DeskThreads({
	proposal,
	designer,
	onDecline,
	onOpenProposal,
	onMessageDesigner,
	isDeclining = false,
}: DeskThreadsProps) {
	const { t } = useTranslation();
	const threadKey = designer ? "two" : "one";

	return (
		<div className="dashboard-container">
			<div className="mb-9">
				<p className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
					ALSO ON YOUR DESK
				</p>
				<h2 className="mt-2 font-display text-[42px] font-medium leading-[1.05] tracking-[-0.02em] text-dashboard-text">
					{t(`homeThreads.${threadKey}.lead`)}{" "}
					<span className="italic text-dashboard-accent-display">
						{t(`homeThreads.${threadKey}.accent`)}
					</span>{" "}
					{t("homeThreads.tail")}
				</h2>
			</div>

			<div className="grid grid-cols-2 gap-5">
				{/* New Proposal Card */}
				<div className="flex flex-col overflow-hidden rounded-2xl border border-dashboard-border dashboard-surface-card-light">
					<div className="relative aspect-16/10 overflow-hidden flex-none">
						<img
							src={
								proposal.designerImageUrl
									? resolveMediaUrl(proposal.designerImageUrl)
									: IMAGE_PLACEHOLDER
							}
							alt={proposal.designerName}
							className="h-full w-full object-cover filter-warm"
							onError={swapToPlaceholder}
						/>
						<div className="absolute inset-0 dashboard-overlay-bottom" />
						<div className="absolute bottom-4 left-4 text-dashboard-on-fill">
							<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-dashboard-on-fill/75">
								NEW PROPOSAL · {proposal.timeAgo}
							</p>
							<span className="mt-1 block font-display text-xl italic font-medium text-dashboard-on-fill">
								{proposal.title}
							</span>
						</div>
					</div>
					<div className="flex flex-col justify-between p-5 flex-1">
						<div className="flex items-center gap-3">
							<div
								className="flex h-9 w-9 items-center justify-center rounded-full font-body text-xs font-bold dashboard-avatar-light"
								style={getAvatarStyle(proposal.designerAvatarStyle)}
							>
								{proposal.designerInitials}
							</div>
							<div className="flex-1">
								<p className="font-display text-base italic font-medium text-dashboard-text">
									{proposal.designerName}
								</p>
								{(proposal.designerStyle ||
									proposal.designerRating !== null) && (
									<p className="font-mono text-[9px] uppercase tracking-[0.14em] text-dashboard-muted">
										{[
											proposal.designerStyle,
											proposal.designerRating !== null
												? `★ ${proposal.designerRating}`
												: null,
										]
											.filter(Boolean)
											.join(" · ")}
									</p>
								)}
							</div>
							<div className="text-right">
								<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
									QUOTE
								</p>
								<p className="font-display text-lg font-medium text-dashboard-text tabular-nums">
									{proposal.amount}
								</p>
							</div>
						</div>
						{proposal.quoteText && (
							<p className="mt-4 font-body text-sm leading-relaxed text-dashboard-muted">
								&ldquo;{proposal.quoteText}&rdquo;
							</p>
						)}
						{proposal.moreCount > 0 && (
							<button
								type="button"
								onClick={() => onOpenProposal?.(proposal.projectId)}
								className="mt-3 self-start border-none bg-transparent p-0 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-dashboard-accent-dark hover:underline"
							>
								{t("homeThreads.moreProposals", { count: proposal.moreCount })}
							</button>
						)}
						<div className="mt-4 flex gap-2">
							<button
								type="button"
								disabled={isDeclining}
								onClick={() => onDecline?.(proposal.proposalId)}
								className="flex-1 rounded-lg border-none bg-none px-2 py-2 font-body text-sm hover:-translate-y-px hover:bg-dashboard-border-soft font-semibold text-dashboard-muted-dark hover:text-dashboard-text disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isDeclining ? "Declining..." : "Decline"}
							</button>
							<button
								type="button"
								onClick={() => onOpenProposal?.(proposal.projectId)}
								className="flex-1 rounded-lg border-none px-12 py-2 font-body text-sm font-semibold text-dashboard-on-fill hover:-translate-y-px dashboard-primary-button"
							>
								Open proposal
							</button>
						</div>
					</div>
				</div>

				{designer && (
					<AssignedDesignerCard
						designer={designer}
						projectId={proposal.projectId}
						onMessageDesigner={onMessageDesigner}
					/>
				)}
			</div>
		</div>
	);
}

interface AssignedDesignerCardProps {
	designer: DashboardDeskDesigner;
	projectId: string;
	onMessageDesigner?: (projectId: string) => void;
}

function AssignedDesignerCard({
	designer,
	projectId,
	onMessageDesigner,
}: AssignedDesignerCardProps) {
	const { t } = useTranslation();
	const eyebrow = [
		designer.isVerified
			? t("homeThreads.verified")
			: t("homeThreads.onYourProject"),
		designer.location?.toUpperCase(),
	]
		.filter(Boolean)
		.join(" · ");

	return (
		<div className="flex flex-col overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-bg">
			<div className="relative aspect-16/10 overflow-hidden flex-none">
				<img
					src={
						designer.imageUrl
							? resolveMediaUrl(designer.imageUrl)
							: IMAGE_PLACEHOLDER
					}
					alt={designer.name}
					className="h-full w-full object-cover filter-warm"
					onError={swapToPlaceholder}
				/>
				<div className="absolute inset-0 dashboard-overlay-bottom" />
				<div className="absolute bottom-4 left-4 text-dashboard-on-fill">
					<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-dashboard-on-fill/75">
						{eyebrow}
					</p>
					<span className="mt-1 block font-display text-xl italic font-medium text-dashboard-on-fill">
						{designer.name}
					</span>
				</div>
			</div>
			<div className="flex flex-col justify-between p-5 flex-1">
				<div className="flex flex-col gap-3">
					<div className="flex items-center gap-3">
						<div
							className="flex h-9 w-9 items-center justify-center rounded-full font-body text-xs font-bold text-dashboard-on-fill dashboard-avatar"
							style={getAvatarStyle(designer.avatarStyle)}
						>
							{designer.initials}
						</div>
						<div className="flex-1">
							<p className="font-display text-base italic font-medium text-dashboard-text">
								{designer.name}
							</p>
							{designer.style && (
								<p className="font-mono text-[9px] uppercase tracking-[0.14em] text-dashboard-muted">
									{designer.style}
								</p>
							)}
						</div>
						{designer.rating !== null && (
							<div className="text-right">
								<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
									RATING
								</p>
								<div className="flex items-center gap-1">
									<Star className="h-3 w-3 fill-dashboard-accent stroke-dashboard-accent" />
									<span className="font-display text-lg font-medium text-dashboard-text">
										{designer.rating}
									</span>
								</div>
							</div>
						)}
					</div>
					{designer.bio && (
						<p className="text-sm font-display italic font-medium text-dashboard-accent-dark">
							&ldquo;{designer.bio}&rdquo;
						</p>
					)}
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => onMessageDesigner?.(projectId)}
						className="flex-1 rounded-lg border-none px-12 py-2 font-body text-sm font-semibold text-dashboard-on-fill hover:-translate-y-px dashboard-primary-button"
					>
						{t("homeThreads.messageDesigner")}
					</button>
				</div>
			</div>
		</div>
	);
}
